import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../contexts/SettingsContext';

const SettingsItem: React.FC<{ icon: React.ReactNode, title: string, subtitle: string, href?: string, action?: React.ReactNode }> = ({ icon, title, subtitle, href, action }) => (
    <div className="flex items-center gap-4 bg-white dark:bg-gray-800 p-4 min-h-[88px]">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-blue-50 dark:bg-gray-700 text-[var(--primary-color)]">
            {icon}
        </div>
        <div className="flex-1">
            <p className="text-base font-medium text-gray-900 dark:text-gray-100">{title}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{subtitle}</p>
        </div>
        {action ? action : (
             <Link to={href || '#'} className="text-gray-400 hover:text-gray-600">
                <svg fill="currentColor" height="20" viewBox="0 0 256 256" width="20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z"></path>
                </svg>
             </Link>
        )}
    </div>
);

const SettingsGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div>
        <h2 className="px-4 pb-2 text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{title}</h2>
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 shadow-sm">
            {children}
        </div>
    </div>
);

const ToggleButton: React.FC<{ options: {value: string, label: string}[], selected: string, onChange: (value: string) => void }> = ({ options, selected, onChange }) => (
    <div className="flex items-center gap-1 rounded-lg bg-gray-100 dark:bg-gray-900 p-1">
        {options.map(({ value, label }) => (
            <button
                key={value}
                onClick={() => onChange(value)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all ${selected === value ? 'bg-white dark:bg-gray-700 text-[var(--primary-color)] shadow-sm' : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-white/50 dark:hover:bg-gray-700/50'}`}
            >
                {label}
            </button>
        ))}
    </div>
);


const Settings: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { settings, setSetting } = useSettings();

    const changeLanguage = (lng: string) => {
        i18n.changeLanguage(lng);
    };

    const languageAction = (
        <ToggleButton
            options={[{ value: 'en', label: t('settings.english') }, { value: 'es', label: t('settings.spanish') }]}
            selected={i18n.language?.startsWith('es') ? 'es' : 'en'}
            onChange={changeLanguage}
        />
    );
    
    const themeAction = (
         <ToggleButton
            options={[{ value: 'light', label: t('settings.themeLight') }, { value: 'dark', label: t('settings.themeDark') }]}
            selected={settings.theme}
            onChange={(value) => setSetting('theme', value as 'light' | 'dark')}
        />
    );

     const desktopNavAction = (
         <ToggleButton
            options={[{ value: 'top', label: t('settings.navTop') }, { value: 'bottom', label: t('settings.navBottom') }]}
            selected={settings.desktopNavLayout}
            onChange={(value) => setSetting('desktopNavLayout', value as 'top' | 'bottom')}
        />
    );
    
    const mobileNavAction = (
         <ToggleButton
            options={[{ value: 'top', label: t('settings.navTop') }, { value: 'bottom', label: t('settings.navBottom') }]}
            selected={settings.mobileNavLayout}
            onChange={(value) => setSetting('mobileNavLayout', value as 'top' | 'bottom')}
        />
    );

    return (
        <div>
            <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">{t('settings.title')}</h1>
            <div className="space-y-8">
                <SettingsGroup title={t('settings.groupApp')}>
                    <SettingsItem href="#" title={t('settings.languageTitle')} subtitle={t('settings.languageSubtitle')} icon={<span className="material-symbols-outlined !text-3xl">translate</span>} action={languageAction} />
                    <SettingsItem href="#" title={t('settings.themeTitle')} subtitle={t('settings.themeSubtitle')} icon={<span className="material-symbols-outlined !text-3xl">contrast</span>} action={themeAction} />
                    <SettingsItem href="#" title={t('settings.desktopNavTitle')} subtitle={t('settings.desktopNavSubtitle')} icon={<span className="material-symbols-outlined !text-3xl">web</span>} action={desktopNavAction} />
                    <SettingsItem href="#" title={t('settings.mobileNavTitle')} subtitle={t('settings.mobileNavSubtitle')} icon={<span className="material-symbols-outlined !text-3xl">phone_iphone</span>} action={mobileNavAction} />
                    <SettingsItem href="#" title={t('settings.preferencesTitle')} subtitle={t('settings.preferencesSubtitle')} icon={<svg fill="currentColor" height="28" viewBox="0 0 256 256" width="28" xmlns="http://www.w3.org/2000/svg"><path d="M216.63,123.53l-18.1-18.1a88,88,0,0,0,0-26.86l18.1-18.1a16,16,0,0,0-4.69-27.32l-22.34-3.72a88.06,88.06,0,0,0-15.54-15.54l-3.72-22.34a16,16,0,0,0-27.32-4.69L125,24.94a87.9,87.9,0,0,0-26.86,0L80.06,6.88a16,16,0,0,0-27.32,4.69L49,33.91a88.06,88.06,0,0,0-15.54,15.54L11.1,53.17a16,16,0,0,0-4.69,27.32l18.1,18.1a88,88,0,0,0,0,26.86l-18.1,18.1a16,16,0,0,0,4.69,27.32l22.34,3.72a88.06,88.06,0,0,0,15.54,15.54l3.72,22.34a16,16,0,0,0,27.32,4.69L98.14,231a87.9,87.9,0,0,0,26.86,0l18.09,18.06a16,16,0,0,0,27.32-4.69l3.72-22.34a88.06,88.06,0,0,0,15.54-15.54l22.34-3.72a16,16,0,0,0,4.69-27.32ZM128,176a48,48,0,1,1,48-48A48.05,48.05,0,0,1,128,176Z"></path></svg>} />
                    <SettingsItem href="#" title={t('settings.notificationsTitle')} subtitle={t('settings.notificationsSubtitle')} icon={<svg fill="currentColor" height="28" viewBox="0 0 256 256" width="28" xmlns="http://www.w3.org/2000/svg"><path d="M224,168v24a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V168H16a8,8,0,0,1,0-16H32V128H16a8,8,0,0,1,0-16H32V88H16a8,8,0,0,1,0-16H32V64A16,16,0,0,1,48,48H208a16,16,0,0,1,16,16V88h16a8,8,0,0,1,0,16H224v24h16a8,8,0,0,1,0,16H224v24h16a8,8,0,0,1,0,16ZM128,80a24,24,0,1,0,24,24A24,24,0,0,0,128,80Zm0,32a8,8,0,1,1,8-8A8,8,0,0,1,128,112Z"></path></svg>} />
                </SettingsGroup>
                 <SettingsGroup title={t('settings.groupAccount')}>
                     <SettingsItem href="#" title={t('settings.accountTitle')} subtitle={t('settings.accountSubtitle')} icon={<svg fill="currentColor" height="28" viewBox="0 0 256 256" width="28" xmlns="http://www.w3.org/2000/svg"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-24-96a8,8,0,0,1,8-8,56,56,0,0,1,56,56,8,8,0,0,1-16,0,40,40,0,0,0-40-40A8,8,0,0,1,104,120Zm83.31,68.69a8,8,0,0,1-1.74,11.14c-11.2,8.41-25,14.17-40.1,16.42a8,8,0,0,1-7.44-8.54,8,8,0,0,1,8.54-7.44c11.22-1.61,21.57-5.9,30.13-12.18A8,8,0,0,1,187.31,188.69Z"></path></svg>} />
                </SettingsGroup>
                <SettingsGroup title={t('settings.groupContent')}>
                    <SettingsItem href="#" title={t('settings.subscriptionsTitle')} subtitle={t('settings.subscriptionsSubtitle')} icon={<svg fill="currentColor" height="28" viewBox="0 0 256 256" width="28" xmlns="http://www.w3.org/2000/svg"><path d="M240,94c0,70-103.79,126.66-108.21,129a16,16,0,0,1-13.8,0C113.79,220.66,8,164,8,94a62,62,0,0,1,112-35.49A62,62,0,0,1,240,94Zm-24,0c0-45.4-41.22-79.61-45.79-82.8A15.8,15.8,0,0,1,168,14a46,46,0,0,0-80,0,15.8,15.8,0,0,1-2.21,2.8C81.22,14.39,40,48.6,40,94c0,42.54,61.13,91.31,88,106.8C154.87,185.31,216,136.54,216,94Z"></path></svg>} />
                </SettingsGroup>
            </div>
        </div>
    );
};

export default Settings;